/*************************************************************************\
Provides 'special' auth transfer functionality (via transfer tokens),
where a single-use 'transfer token' is created using the current (griffin)
auth session and sent to another origin (domain) which will then verify the
token and (if valid) create an auth session on that other domain.

This functionality allows for the user to avoid having to sign-in multiple
times when transfering between origins (domains).

This functionality should (hopefully) only be needed while legacy systems
are operating (ie phoenix), once everything uses (griffin) APIs then we
shouldn't need these except possibly for special purpose scenarios.
\*************************************************************************/

import { config } from '../app/config.js';
import { log } from '../app/log.js';

const phoenixAdminAuthApi = config.endpoints.phoenixAdmin + 'auth.api';

function isAuthAlreadyOnPhoenixAdmin() {
    // Get the current auth status on phoenix/admin, if already auth'd then don't need to do it again.
    return fetch(phoenixAdminAuthApi, {
        method: 'get',
        mode: 'cors',
        cache: 'no-store',
        credentials: 'include'
    })
        // Either null (if failure) or a promise to get result data as a string.
        .then(function (result) { return !result.ok ? null : result.text() })
        // Return if already is authenticated on phoenix/admin, or if not.
        .then(function (resultText) { return resultText && resultText.match(/"AuthSuccess"/i); }); // See phoenix/libauth/AuthHandler.cs (AuthSuccess & AuthFailure)
};

function getTransferTokenPromise(refresh) {
    return new Promise(function (resolve) {
        // Refresh our current (griffin) auth session AND specify a transferTokenCallback
        // which will cause the refresh to create a transfer token and call the callback once ready.
        refresh(resolve);
    });
}
function putTransferTokenOnPhoenixAdmin(data) {
    if (!data || !data.uniqueID || !data.transferToken) {
        log.error('auth-transfer.authTransferTokenOnPhoenixAdmin{token failure}', data);
        return false; // Failure
    }
    else {
        // Otherwise the transfer token was retrieved so we can now
        // put it on phoenix/admin to create the auth session there.
        return fetch(phoenixAdminAuthApi, {
            method: 'put',
            mode: 'cors',
            cache: 'no-store',
            credentials: 'include',
            body: createFormData({
                id: data.uniqueID,
                token: data.transferToken
            })
        })
            .then(function (response) {
                if (response.ok)
                    return true;
                else {
                    log.error('auth-transfer.authTransferTokenOnPhoenixAdmin{put failure}', response.status);
                    return false;
                }
            });
    }
}
function createFormData(fieldsObject) {
    const formFields = new FormData();
    for (const k in fieldsObject) {
        formFields.append(k, fieldsObject[k]);
    }
    return formFields;
}

export const transfer = {
    toPhoenixAdmin(refresh) {
        return isAuthAlreadyOnPhoenixAdmin()
            .then(function (alreadyAuthenticated) {
                if (alreadyAuthenticated)
                    return true;
                else
                    return getTransferTokenPromise(refresh)
                        .then(putTransferTokenOnPhoenixAdmin);
            })
            .catch(function (ex) {
                log.error('auth-transfer.transfer.toPhoenixAdmin{exception}', ex);
                return false;
            });
    }
};